import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BillItem,
  BillType,
  ITables,
  KITCHEN_ITEM_STATUS,
  ORDER_TYPE,
  TAKEAWAY_STATUS,
} from '../../all-types'
import { useSocketEvent } from '../../realtime'
import { updateAllTables } from '../../redux-store'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import { getBills, getTableList } from '../../services'
import { getDayTimestamps } from '../../utils'
import { DineInKanban } from './components/DineInKanban'
import { TakeawayOrdersDisplay } from './take-away-orders-display'

export type KitchenTabValue = 'dineIn' | 'takeaway'

// Colors for chip side stripe
const COLOR_RED = '#E53935'
const COLOR_ORANGE = '#FB8C00'
const COLOR_GREEN = '#43A047'

const KitchenTabsScreen: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<KitchenTabValue>('dineIn')
  const [takeawayBills, setTakeawayBills] = useState<BillType[]>([])
  const [query, setQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [tableFilter, setTableFilter] = useState<string>('all')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const allTables = useAppSelector((state) => state.preloadData.tables)
  const dispatch = useAppDispatch()

  useSocketEvent<BillType[]>('tables:takeaway', (bills) => {
    setTakeawayBills(bills)
  })

  useSocketEvent<ITables[]>('tables:list', (tables) => {
    dispatch(updateAllTables(tables))
  })

  const loadKitchenData = useCallback(async () => {
    try {
      const timestamp = getDayTimestamps()
      const response = await getBills(timestamp.morning, timestamp.night)
      if (response?.bills) {
        const ongoingTakeawayBills = response.bills.filter(
          (b: BillType) =>
            b?.orderType === ORDER_TYPE.TAKEAWAY &&
            b.takeAwayStatus !== TAKEAWAY_STATUS.DONE
        )
        setTakeawayBills(ongoingTakeawayBills)
      }
      await dispatch(getTableList())
    } catch (e) {
      console.error('Kitchen load failed:', e)
    }
  }, [dispatch])

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      await loadKitchenData()
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    loadKitchenData()
  }, [loadKitchenData])

  // -------- Notifications: only on transition TO IN_KITCHEN --------
  const activeDineInTables = useMemo(
    () =>
      allTables.filter(
        (t) =>
          t.billSession && t.billSession.items && t.billSession.items.length > 0
      ),
    [allTables]
  )

  type ItemStatusMap = Record<string, KITCHEN_ITEM_STATUS | string>
  const prevItemsRef = useRef<ItemStatusMap | null>(null)

  const buildItemStatusMap = (): ItemStatusMap => {
    const map: ItemStatusMap = {}
    for (const t of activeDineInTables) {
      const items = t?.billSession?.items || []
      for (const it of items) {
        const id =
          it?.sno || `${t.tableNo}:${it?.itemName}:${it?.createdAt || ''}`
        map[id] = it?.kitchInfo?.status as KITCHEN_ITEM_STATUS
      }
    }
    for (const b of takeawayBills || []) {
      const items = b?.items || []
      for (const it of items) {
        const id =
          it?.sno ||
          `TA:${b.billNo || b._id}:${it?.itemName}:${it?.createdAt || ''}`
        map[id] = it?.kitchInfo?.status as KITCHEN_ITEM_STATUS
      }
    }
    return map
  }

  useEffect(() => {
    const current = buildItemStatusMap()
    if (!prevItemsRef.current) {
      prevItemsRef.current = current
      return
    }
    const prev = prevItemsRef.current
    let shouldNotify = false
    for (const [id, curr] of Object.entries(current)) {
      const prevStatus = prev[id]
      const nowInKitchen =
        curr === KITCHEN_ITEM_STATUS.IN_KITCHEN || curr === 'IN_KITCHEN'
      const wasNotInKitchen =
        prevStatus !== KITCHEN_ITEM_STATUS.IN_KITCHEN &&
        prevStatus !== 'IN_KITCHEN'
      if (nowInKitchen && (prevStatus === undefined || wasNotInKitchen)) {
        shouldNotify = true
        break
      }
    }
    if (
      shouldNotify &&
      typeof window !== 'undefined' &&
      'Notification' in window
    ) {
      try {
        if (Notification.permission === 'granted') {
          new Notification('Order Update!', {
            body: 'An item moved to In-Kitchen.',
            icon: '/icon.png',
          })
          const audio = new Audio('/order-noti.mp3')
          audio.play().catch(() => {})
        }
      } catch (e) {
        console.error('Error showing notification:', e)
      }
    }
    prevItemsRef.current = current
  }, [activeDineInTables, takeawayBills])

  // -------- Helpers to classify table status --------
  const STATUS_OF_INTEREST = [
    KITCHEN_ITEM_STATUS.IN_KITCHEN,
    KITCHEN_ITEM_STATUS.COOKING,
    KITCHEN_ITEM_STATUS.READY,
  ]

  const classifyTable = (items: BillItem[]) => {
    const statuses = new Set(
      items
        .map((i) => i.kitchInfo?.status as KITCHEN_ITEM_STATUS)
        .filter((s) => STATUS_OF_INTEREST.includes(s))
    )
    if (statuses.size === 0) return null // no relevant items
    if (statuses.size === 1 && statuses.has(KITCHEN_ITEM_STATUS.READY))
      return 'green'
    if (statuses.size === 1 && statuses.has(KITCHEN_ITEM_STATUS.IN_KITCHEN))
      return 'red'
    return 'orange' // any cooking or mix
  }

  // -------- Tab badges --------
  const dineInAttentionCount = useMemo(() => {
    let count = 0
    for (const t of activeDineInTables) {
      const items = (t?.billSession?.items || []).filter((i) =>
        STATUS_OF_INTEREST.includes(i.kitchInfo?.status as KITCHEN_ITEM_STATUS)
      )
      const cls = classifyTable(items)
      if (cls === 'red' || cls === 'orange') count += 1
    }
    return count
  }, [activeDineInTables])

  const takeAwayPendingCount = useMemo(
    () =>
      (takeawayBills || []).filter((b) => b.takeAwayStatus === 'PENDING')
        .length,
    [takeawayBills]
  )

  // -------- Search + dynamic table chips (shown ONLY on Dine-In) --------
  // flatten dine-in, apply search, then keep only three statuses of interest
  const allDineInItems: (BillItem & { tableNo: number })[] = useMemo(() => {
    const out: (BillItem & { tableNo: number })[] = []
    for (const t of activeDineInTables) {
      for (const it of t?.billSession?.items || [])
        out.push({ ...it, tableNo: t.tableNo })
    }
    return out
  }, [activeDineInTables])

  const textFiltered = useMemo(() => {
    if (!query) return allDineInItems
    const q = query.toLowerCase()
    return allDineInItems.filter(
      (i) =>
        i.itemName.toLowerCase().includes(q) || String(i.tableNo).includes(q)
    )
  }, [allDineInItems, query])

  const statusFilteredForHeader = useMemo(
    () =>
      textFiltered.filter((i) =>
        STATUS_OF_INTEREST.includes(i.kitchInfo?.status as KITCHEN_ITEM_STATUS)
      ),
    [textFiltered]
  )

  // Build classification per table (AFTER search), then split into attention vs ready
  const headerTableGroups = useMemo(() => {
    const byTable = new Map<number, BillItem[]>()
    for (const i of statusFilteredForHeader) {
      const list = byTable.get(i.tableNo) || []
      list.push(i as BillItem)
      byTable.set(i.tableNo, list)
    }
    const attention: Array<{ tableNo: number; color: string }> = []
    const ready: Array<{ tableNo: number; color: string }> = []
    for (const [tNo, items] of byTable.entries()) {
      const cls = classifyTable(items)
      if (!cls) continue
      if (cls === 'green') ready.push({ tableNo: tNo, color: COLOR_GREEN })
      else if (cls === 'red') attention.push({ tableNo: tNo, color: COLOR_RED })
      else attention.push({ tableNo: tNo, color: COLOR_ORANGE })
    }
    attention.sort((a, b) => a.tableNo - b.tableNo)
    ready.sort((a, b) => a.tableNo - b.tableNo)
    return { attention, ready }
  }, [statusFilteredForHeader])

  // Keep selection valid
  useEffect(() => {
    if (selectedTab !== 'dineIn') return
    const allVisible = new Set([
      ...headerTableGroups.attention.map((t) => t.tableNo),
      ...headerTableGroups.ready.map((t) => t.tableNo),
    ])
    if (tableFilter !== 'all' && !allVisible.has(Number(tableFilter))) {
      setTableFilter('all')
    }
  }, [headerTableGroups, tableFilter, selectedTab])

  const handleTabChange = (_: React.SyntheticEvent, v: KitchenTabValue) =>
    setSelectedTab(v)

  const handleSearchToggle = () => {
    if (isSearchOpen) setQuery('')
    setIsSearchOpen((open) => !open)
  }

  const TabLabel = (text: string, count: number, active: boolean) => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography fontSize={18} fontWeight={900}>
        {text}
      </Typography>
      <Box
        component="span"
        sx={{
          minWidth: 32,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          bgcolor: active ? 'primary.contrastText' : 'action.selected',
          color: active ? 'primary.main' : 'text.primary',
          fontSize: 15,
          fontWeight: 900,
          lineHeight: 1.4,
          textAlign: 'center',
        }}
      >
        {count}
      </Box>
    </Stack>
  )

  return (
    <Box p={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, gap: 2 }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 58,
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab
            label={TabLabel('Dine-In', dineInAttentionCount, selectedTab === 'dineIn')}
            value="dineIn"
            sx={{
              textTransform: 'none',
              minHeight: 52,
              px: 2.5,
              mr: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          />
          <Tab
            label={TabLabel('Take-Away', takeAwayPendingCount, selectedTab === 'takeaway')}
            value="takeaway"
            sx={{
              textTransform: 'none',
              minHeight: 52,
              px: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          />
        </Tabs>

        {selectedTab === 'dineIn' && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ flexWrap: 'wrap' }}
          >
            {/* Dynamic table chips: Attention (red/orange) | divider | Ready (green) */}
            <ToggleButtonGroup
              exclusive
              value={tableFilter}
              onChange={(_, v) => v && setTableFilter(v)}
              size="medium"
              sx={{ gap: 0.75, flexWrap: 'wrap' }}
            >
              <ToggleButton
                value="all"
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  minHeight: 44,
                  minWidth: 64,
                  fontSize: 16,
                  fontWeight: 700,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                }}
              >
                All
              </ToggleButton>

              {headerTableGroups.attention.map(({ tableNo, color }) => (
                <ToggleButton
                  key={`att-${tableNo}`}
                  value={String(tableNo)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    minHeight: 70,
                    minWidth: 70,
                    fontSize: 16,
                    fontWeight: 700,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 6,
                      top: 8,
                      bottom: 8,
                      width: 8,
                      borderRadius: 2,
                      backgroundColor: color,
                    },
                    pl: 3,
                    '&.Mui-selected': {
                      bgcolor: color,
                      color: '#FFFFFF',
                      borderColor: color,
                      boxShadow: 2,
                      '&::before': {
                        backgroundColor: '#FFFFFF',
                      },
                      '&:hover': {
                        bgcolor: color,
                        filter: 'brightness(0.95)',
                      },
                    },
                  }}
                >
                  {tableNo}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Divider between attention and green group */}
            {headerTableGroups.ready.length > 0 && (
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            )}

            <ToggleButtonGroup
              exclusive
              value={tableFilter}
              onChange={(_, v) => v && setTableFilter(v)}
              size="medium"
              sx={{ gap: 0.75, flexWrap: 'wrap' }}
            >
              {headerTableGroups.ready.map(({ tableNo }) => (
                <ToggleButton
                  key={`ready-${tableNo}`}
                  value={String(tableNo)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    minHeight: 70,
                    minWidth: 70,
                    fontSize: 16,
                    fontWeight: 700,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 6,
                      top: 8,
                      bottom: 8,
                      width: 8,
                      borderRadius: 2,
                      backgroundColor: COLOR_GREEN,
                    },
                    pl: 3,
                    '&.Mui-selected': {
                      bgcolor: COLOR_GREEN,
                      color: '#FFFFFF',
                      borderColor: COLOR_GREEN,
                      boxShadow: 2,
                      '&::before': {
                        backgroundColor: '#FFFFFF',
                      },
                      '&:hover': {
                        bgcolor: COLOR_GREEN,
                        filter: 'brightness(0.95)',
                      },
                    },
                  }}
                >
                  {tableNo}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          {selectedTab === 'dineIn' && (
            <>
              {isSearchOpen && (
                <TextField
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search items or table..."
                  variant="outlined"
                  size="small"
                  sx={{
                    width: { xs: 180, sm: 240 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Tooltip
                title={isSearchOpen ? 'Hide search' : 'Search kitchen items'}
              >
                <IconButton
                  onClick={handleSearchToggle}
                  color={isSearchOpen ? 'primary' : 'default'}
                  sx={{
                    border: '1px solid',
                    borderColor: isSearchOpen ? 'primary.main' : 'divider',
                    width: 42,
                    height: 42,
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title="Refresh kitchen">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 42,
                  height: 42,
                }}
              >
                {isRefreshing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {selectedTab === 'dineIn' && (
        <DineInKanban
          tables={activeDineInTables}
          query={query}
          tableFilter={tableFilter}
        />
      )}

      {selectedTab === 'takeaway' && (
        <TakeawayOrdersDisplay
          takeawayBills={takeawayBills}
          setTakeawayBills={setTakeawayBills}
        />
      )}
    </Box>
  )
}

export { KitchenTabsScreen }
